import axios from 'axios';
import {
  GET_USERS,
  LIST_ENTITY_PROFILES_URL,
  VIEW_ENTITY_TYPE_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  listXVerdictEntityConfigs,
} from '@/services/xverdict_entity_config_service';
import { fetch_requestor_application_forms } from '@/services/requestor_portal_service';
import { mapEntityConfigToEntityType } from '@/services/xverdict_config_mapper';
import {
  get_entity_type_name,
  get_record_status,
  normalize_key,
  normalize_text,
  object_label,
  parse_response_data,
  read_session_current_role,
  read_session_current_user_id,
  to_array,
  to_number,
} from './company_cases_common';
import {
  domain_badge_class,
  format_case_code,
  format_subtitle,
  format_title,
  record_created_date,
  status_dot_class,
} from './company_cases_formatters';
import { status_dot_style } from '@/views/x-verdict_views/CaseOwner/AllCases/case_state';
import { owner_label } from './company_cases_owner_helpers';
import { risk_details } from './company_cases_risk_helpers';
import { sla_details } from './company_cases_sla_helpers';

const ENTITY_PROFILE_CACHE_TTL_MS = 60000;
const entity_profile_pending_requests = new Map();
const entity_profile_response_cache = new Map();

function case_domain(record = {}, entity_type = {}) {
  const labels = [
    record?.entity_type_name,
    record?.entity_type_label,
    record?.domain_name,
    object_label(record?.entity_type),
    record?.domain,
    get_entity_type_name(entity_type),
    'Case',
  ];
  return normalize_text(labels.find(Boolean));
}

function backend_case_ids(record = {}, entity_type = {}) {
  return {
    project_id: to_number([
      record?.project_id,
      record?.project?.id,
      record?.project,
      record?.entity?.project_id,
      record?.entity?.project?.id,
      entity_type?.project_id,
      entity_type?.project?.id,
    ].find(Boolean)),
    application_id: to_number([
      record?.application_id,
      record?.latest_application_id,
      record?.current_application_id,
    ].find(Boolean)),
    entity_profile_id: to_number([record?.entity_profile_id, record?.id].find(Boolean)),
    entity_type_id: to_number([
      record?.entity_type_id,
      record?.entity_type?.id,
      record?.entity_type,
      entity_type?.id,
    ].find(Boolean)),
  };
}

function has_real_case_application(record = {}, entity_type = {}) {
  return Boolean(
    record?.has_entity_form
    && backend_case_ids(record, entity_type).application_id
  );
}

function sort_timestamp(record = {}) {
  const timestamp = new Date(record_created_date(record) ?? 0).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export function map_backend_case(record = {}, entity_type = {}, users_by_id = new Map()) {
  const ids = backend_case_ids(record, entity_type);
  const domain = case_domain(record, entity_type);
  const domain_key = normalize_key(domain);
  const status = get_record_status(record);

  return {
    case_code: format_case_code(record),
    ...ids,
    application_assigned_users: to_array(record?.application_assigned_users),
    project_name: normalize_text(
      record?.project_name
      || record?.project?.project_name
      || record?.project?.name
      || record?.entity?.project_name
      || record?.entity?.project?.project_name
      || record?.entity?.project?.name
      || entity_type?.project_name
      || entity_type?.project?.project_name
      || entity_type?.project?.name
    ),
    assignment_user_ids: to_array(record?.assignment_user_ids).map((value) => to_number(value)).filter(Boolean),
    title: format_title(record, domain),
    subtitle: format_subtitle(record),
    domain,
    domain_key,
    domain_badge_class: domain_badge_class(domain_key),
    status,
    status_color: record?.application_status_color
      || record?.status_color
      || record?.application_status?.color_code
      || record?.status?.color_code
      || null,
    status_dot_class: status_dot_class(status),
    status_dot_style: status_dot_style(
      record?.application_status_color
      || record?.status_color
      || record?.application_status?.color_code
      || record?.status?.color_code
    ),
    owner: owner_label(record, users_by_id),
    sort_time: sort_timestamp(record),
    ...risk_details(record),
    ...sla_details(record),
  };
}

function application_question_answer_text(question = {}) {
  const raw_answer = question?.answer;
  if (Array.isArray(raw_answer)) return raw_answer.join(', ');
  if (typeof raw_answer === 'object' && raw_answer !== null) {
    return normalize_text(raw_answer?.file_name || raw_answer?.original_url || '');
  }
  return normalize_text(raw_answer);
}

function first_application_answer_text(application_forms = []) {
  return normalize_text(
    to_array(application_forms)
      .flatMap((application_form) => to_array(application_form?.form?.sections)
        .flatMap((section) => to_array(section?.questions)))
      .map(application_question_answer_text)
      .find(Boolean)
  );
}

export async function hydrate_case_display_title(case_item = {}) {
  if (!case_item?.application_id) {
    return case_item;
  }

  try {
    const application_forms = await fetch_requestor_application_forms(case_item.application_id);
    const title = first_application_answer_text(application_forms);
    return title ? { ...case_item, title } : case_item;
  } catch {
    return case_item;
  }
}

export function is_real_case_record(record = {}, entity_type = {}) {
  return has_real_case_application(record, entity_type);
}

async function configured_entity_types(params = {}) {
  const config_rows = await listXVerdictEntityConfigs(params);
  return config_rows.map(mapEntityConfigToEntityType).filter(Boolean);
}

export async function fetch_entity_types(params = {}) {
  try {
    const entity_types = await configured_entity_types(params);
    if (entity_types.length) return entity_types;
  } catch {
    // Legacy listing supports tenants whose entity types are not configured yet.
  }

  const response = await axios.get(VIEW_ENTITY_TYPE_URL, get_request_config({ params }));
  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed).filter((entity_type) => to_number(entity_type?.id));
}

async function fetch_entity_profile_page(params = {}) {
  const response = await axios.get(LIST_ENTITY_PROFILES_URL, get_request_config({ params }));
  return parse_response_data(response);
}

function clone_entity_profile_records(records = []) {
  return to_array(records).map((record) => ({ ...record }));
}

function normalized_entity_profile_params(params = {}) {
  const normalized = { ...params };
  delete normalized.page;
  delete normalized.page_size;
  normalized.include_assignment_user_ids = true;
  normalized.include_application_assigned_users = true;
  normalized.include_sla_summary = true;
  normalized.include_risk_summary = true;
  return Object.fromEntries(
    Object.entries(normalized)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .sort(([left], [right]) => left.localeCompare(right))
  );
}

function entity_profile_cache_key(params = {}) {
  return JSON.stringify(normalized_entity_profile_params(params));
}

function cached_entity_profile_records(cache_key) {
  const entry = entity_profile_response_cache.get(cache_key);
  if (!entry) return null;
  if (entry.expires_at <= Date.now()) {
    entity_profile_response_cache.delete(cache_key);
    return null;
  }
  return clone_entity_profile_records(entry.records);
}

export function clear_entity_profile_list_cache() {
  entity_profile_pending_requests.clear();
  entity_profile_response_cache.clear();
}

function is_coordinator_role(role) {
  return normalize_text(role).toLowerCase().includes('coordinator');
}

function normalize_user_scope(user = {}) {
  return {
    user_id: to_number(user?.user_id || user?.id || user?.user?.id),
    project_id: to_number(user?.project_id || user?.project?.id || user?.project),
    project_name: normalize_text(user?.project_name || user?.project?.project_name || user?.project?.name),
    entity_type_id: to_number(user?.entity_type_id),
    entity_type_name: normalize_text(user?.entity_type_name),
    role_name: normalize_text(user?.role_name || user?.role || user?.user?.role?.role),
  };
}

async function fetch_users_page(params = {}) {
  const response = await axios.get(GET_USERS, get_request_config({ params }));
  return parse_response_data(response);
}

async function find_user_scope_in_listing(params = {}) {
  const current_user_id = read_session_current_user_id();
  if (!current_user_id) return null;

  let page = 1;
  let total_pages = 1;

  do {
    const parsed = await fetch_users_page({ page, page_size: 100, ...params });
    const users = to_array(parsed?.data || parsed).map(normalize_user_scope);
    const matched_users = users.filter((user) => user.user_id === current_user_id);
    const matched_user = matched_users.find((user) => user.entity_type_id) || matched_users[0];
    if (matched_user) {
      return matched_user;
    }

    total_pages = to_number(parsed?.pagination?.total_pages) || 1;
    page += 1;
  } while (page <= total_pages);

  return null;
}

export async function fetch_current_user_case_scope(project_id = null) {
  const scoped_user = await find_user_scope_in_listing(
    project_id ? { project_id } : {}
  );
  if (scoped_user?.entity_type_id) {
    return scoped_user;
  }

  const fallback_user = scoped_user || await find_user_scope_in_listing();
  if (!fallback_user) {
    return null;
  }

  if (fallback_user.entity_type_id) {
    return fallback_user;
  }

  if (is_coordinator_role(fallback_user.role_name) || is_coordinator_role(read_session_current_role())) {
    return fallback_user;
  }

  return null;
}

function positive_number_or(value, fallback) {
  return to_number(value) ?? fallback;
}

export async function fetch_entity_profiles(params = {}) {
  const page_size = positive_number_or(params.page_size, 100);
  const cache_key = entity_profile_cache_key(params);
  const cached_records = cached_entity_profile_records(cache_key);
  if (cached_records) return cached_records;

  if (entity_profile_pending_requests.has(cache_key)) {
    return entity_profile_pending_requests.get(cache_key).then(clone_entity_profile_records);
  }

  const request = (async () => {
    let page = positive_number_or(params.page, 1);
    let total_pages = 1;
    const records = [];

    do {
      const parsed = await fetch_entity_profile_page({
        ...params,
        include_assignment_user_ids: true,
        include_application_assigned_users: true,
        include_sla_summary: true,
        include_risk_summary: true,
        page,
        page_size,
      });
      records.push(...to_array(parsed?.data || parsed));
      total_pages = positive_number_or(parsed?.pagination?.total_pages, 1);
      page += 1;
    } while (page <= total_pages);

    entity_profile_response_cache.set(cache_key, {
      records: clone_entity_profile_records(records),
      expires_at: Date.now() + ENTITY_PROFILE_CACHE_TTL_MS,
    });
    return records;
  })();

  entity_profile_pending_requests.set(cache_key, request);

  try {
    return clone_entity_profile_records(await request);
  } finally {
    entity_profile_pending_requests.delete(cache_key);
  }
}

export async function fetch_scoped_entity_types(project_id = null) {
  const coordinator_scope = await fetch_current_user_case_scope(project_id);
  const is_coordinator = is_coordinator_role(
    coordinator_scope?.role_name || read_session_current_role()
  );
  const requested_project_id = to_number(project_id);

  if (!is_coordinator) {
    return {
      entity_types: await fetch_entity_types(project_id ? { project_id } : {}),
      coordinator_scope: null,
    };
  }

  if (
    !coordinator_scope?.project_id
    || (requested_project_id && coordinator_scope.project_id !== requested_project_id)
  ) {
    return {
      entity_types: [],
      coordinator_scope,
    };
  }

  return {
    entity_types: await fetch_entity_types({
      project_id: requested_project_id || coordinator_scope.project_id,
    }),
    coordinator_scope,
  };
}

export { axios, get_request_config };
